import { DateTime } from "luxon";

const formatTime12Hour = (dateTime: DateTime): string => {
  const hour = dateTime.hour;
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const minute = dateTime.minute.toString().padStart(2, "0");
  return `${hour12}:${minute}`;
};

const formatSingleTime = (dateTime: DateTime): string => {
  return dateTime
    .toLocaleString({
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase()
    .replace(" ", "");
};

export const formatCalendarEventTime = (
  startTime: string | undefined | null,
  endTime: string | undefined | null,
  isAllDay: boolean = false
): string => {
  if (isAllDay) return "All day";
  if (!startTime) return "Unknown time";

  const startDateTime = DateTime.fromISO(startTime);
  if (!startDateTime.isValid) return "Invalid time";

  if (!endTime) return formatSingleTime(startDateTime);

  const endDateTime = DateTime.fromISO(endTime);
  if (!endDateTime.isValid) return formatSingleTime(startDateTime);

  if (startDateTime.hasSame(endDateTime, "minute")) return "";

  const startTimeStr = formatTime12Hour(startDateTime);
  const endTimeStr = formatTime12Hour(endDateTime);
  const startIsAM = startDateTime.hour < 12;
  const endIsAM = endDateTime.hour < 12;

  // Format AM/PM: don't repeat if both are the same period
  let formattedStart = startTimeStr;
  let formattedEnd = endTimeStr;

  if (startIsAM && endIsAM) {
    // Both AM - am only on end
    formattedEnd += "am";
  } else if (!startIsAM && !endIsAM) {
    // Both PM - pm only on end
    formattedEnd += "pm";
  } else {
    // Mixed - show both
    formattedStart += startIsAM ? "am" : "pm";
    formattedEnd += endIsAM ? "am" : "pm";
  }

  if (startDateTime.hasSame(endDateTime, "day")) {
    return `${formattedStart}-${formattedEnd}`;
  }

  // Different days - show dates with times
  const formatOptions = {
    month: "short" as const,
    day: "numeric" as const,
    hour: "numeric" as const,
    minute: "2-digit" as const,
  };
  return `${startDateTime.toLocaleString(formatOptions)} - ${endDateTime.toLocaleString(formatOptions)}`;
};

export const formatCalendarEventDate = (startTime: string): string => {
  if (!startTime) return "";

  const startDateTime = DateTime.fromISO(startTime);
  if (!startDateTime.isValid) return "";

  return startDateTime.toLocaleString({
    weekday: "long",
    month: "short",
    day: "numeric",
  });
};
